/** ---------------------------------------------------------------------------------------
 * --  LICENSE AGREEMENT -- *
 This software is licensed under the following terms:

 * -- GRANT OF LICENSE | RESTRICTIONS -- *
 Subject to the terms and conditions of this license, the copyright holders
 hereby grant you a non-exclusive, non-transferable, revocable license to use,
 copy, modify, and distribute the software.
 You are expressly prohibited from selling or commercially exploiting the software.
 Unauthorized use, reproduction, or distribution of the software, or any part
 thereof, is strictly prohibited.

 * -- AUTHORIZED USERS | INTELLECTUAL PROPERTY -- *
 This license is granted only to the individual or entity to whom the software is
 directly distributed by the copyright holders. Any use by other individuals or
 entities is unauthorized and in violation of this license. The software is
 protected by copyright and other intellectual property laws.
 The copyright holders retain all rights, title, and interest in the software.

 * -- GOVERNING LAW -- *
 This license is governed by and construed in accordance with the laws of Romania.
 Any legal action or proceeding related to this license shall be brought exclusively
 in the courts of Romania.

 [Weddify x Diaphan Stationery] © [2024] All rights reserved.
 -------------------------------------------------------------------------------------- **/

import React, {Fragment, useEffect, useRef, useState} from "react";
import {IoCloseSharp} from "react-icons/io5";
import {PiInfoLight} from "react-icons/pi";
import {Dialog, Transition} from "@headlessui/react";
import AppStatic from "@/utils/AppStatic";

interface Tooltip {
    data: any;
    image?: string;
    video?: string;
    onlyVideo?: boolean;
    showExternal?: boolean;
    onClose?: () => void;
}

const AppTooltip = ({data, image, video, onlyVideo, showExternal, onClose}: Tooltip) => {
    const [open, setOpen] = useState(false)
    const cancelButtonRef = useRef(null)

    const openTooltipHandler = () => {
        setOpen(true);
    }

    const handleClose = () => {
        setOpen(false);
        if (onClose) {
            onClose();
        }
    }

    useEffect(() => {
        if (showExternal) {
            setOpen(showExternal);
        }
    }, [showExternal])

    return (
        <>
            <div
                className="p-0.5 justify-center rounded-full cursor-pointer hover:scale-105 transition-transform ease-in-out duration-300"
                onClick={openTooltipHandler}>
                <PiInfoLight className="text-2xl text-black"/>
            </div>
            <Transition.Root show={open} as={Fragment}>
                <Dialog as="div" className="relative z-50" initialFocus={cancelButtonRef} onClose={() => handleClose()}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500/75 transition-opacity"/>
                    </Transition.Child>
                    <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
                        <div
                            className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel
                                    className={`${onlyVideo ? `w-95p md:w-80p` : `sm:w-full sm:max-w-lg`} relative transform overflow-scroll rounded-lg bg-mono-0 dark:bg-mono-1000 text-left shadow-xl transition-all sm:my-8`}>
                                    <div className="bg-mono-0 dark:bg-mono-1000 px-4 pb-4 pt-4 sm:pb-4">
                                        <div className={`${!onlyVideo && `sm:flex sm:items-start`}`}>
                                            <div
                                                className={`mt-3 text-center ${!onlyVideo && `sm:ml-4 sm:mt-0 sm:text-left`}`}>
                                                <div className="mt-2">
                                                    <div className="text-md text-mono-1000 dark:text-mono-0">
                                                        {data}
                                                        {image ? (<img className="mt-4" src={image}
                                                                       alt="Weddify by Diaphan"/>) : null}
                                                        {video && (
                                                            <div
                                                                className={`${onlyVideo && `relative mt-0! flex`} mt-4`}>
                                                                <video
                                                                    className="w-full h-full"
                                                                    src={video}
                                                                    playsInline={true} muted={true} loop={false} controls={true} autoPlay={true}></video>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {onlyVideo ? (
                                        <button
                                            className="absolute right-1 top-1 cursor-pointer md:hover:scale-105 transition z-1000"
                                            ref={cancelButtonRef}
                                            onClick={() => handleClose()}>
                                            <IoCloseSharp className="text-3xl"/>
                                        </button>
                                    ) : (
                                        <div className="bg-mono-0 px-4 py-4 sm:flex sm:flex-row-reverse">
                                            <button
                                                type="button"
                                                className="cursor-pointer mt-3 inline-flex w-full justify-center rounded-lg bg-mono-0 dark:bg-mono-1000 px-3 py-2 text-sm font-semibold text-mono-1000 dark:text-mono-0 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                                onClick={() => handleClose()}
                                                ref={cancelButtonRef}>
                                                {AppStatic.tooltipDismiss.ro.translation}
                                            </button>
                                        </div>
                                    )}
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
        </>
    );
}

export default AppTooltip;
